import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CARCASS_ANIMALS,
  CARCASS_TYPES,
  standardCutPercent,
  voidReasonSchema,
  type CarcassAnimal,
  type CarcassLotDTO,
  type CutTemplateDTO,
  type CuttingRunDTO,
  type ExternalWorkerDTO,
  INSIGHT_CONFIDENCE_LABELS,
  type CarcassInsightDTO,
  type CuttingForecastDTO,
  type CuttingInsightsDTO,
  type SupplierInsightDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Beef, BrainCircuit, History, ListChecks, Plus, Scale, Sparkles, Tag, Trash2, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatDateTime, formatKilos, formatMoney } from '../../lib/format';
import { useCatalogAccess, useProducts } from '../../lib/catalog';
import { useHardware } from '../../lib/hardware';
import { connectScale, scaleFresh, useScale } from '../../lib/scale-link';
import { useSession } from '../../lib/session';
import { fromInternalQty, toInternalQty } from '../../lib/units';

type Tab = 'cut' | 'history' | 'yields' | 'templates';
const pct = (r: number) => `${(r * 100).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

function useTemplates(enabled = true) {
  return useQuery({ queryKey: ['cutting', 'templates'], queryFn: () => api.get<CutTemplateDTO[]>('/cutting/templates'), enabled });
}

/**
 * Desposte (adicional): la media res se convierte en cortes con stock, costo y ganancia propios.
 * Todo se pesa: no se usan pesos estimados para calcular costos.
 */
export function CuttingPage() {
  const { me } = useSession();
  const [tab, setTab] = useState<Tab>('cut');
  const enabled = me.tenant.modules.includes('CUTTING');
  const templates = useTemplates(true);

  const list = templates.data ?? [];
  return (
    <>
      <PageHeader
        title={enabled ? 'Desposte automático' : 'Desposte manual'}
        subtitle={
          enabled
            ? 'Pesás cada corte y el sistema hace el resto: stock, costo real por corte, ganancia, precio sugerido y rinde de cada media res.'
            : 'Planilla a mano: escribís los kilos de cada corte (y tu % y tu rinde si los calculás) y con un botón los cortes entran al stock.'
        }
      />
      {!enabled && (
        <Callout>
          El <strong>desposte automático</strong> es un adicional: balanza conectada, costo real de cada corte, rinde por pieza y por proveedor, aviso
          de bajo rinde y precio sugerido. Pedíselo a BifeAI.
        </Callout>
      )}
      <div className="bf-tabs" role="tablist">
        {(
          [
            ['cut', 'Para despostar'],
            ['history', 'Historial'],
            ['yields', 'Frigoríficos'],
            ['templates', 'Tipos y cortes'],
          ] as const
        )
          .filter(([k]) => enabled || k !== 'yields')
          .map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
      </div>
      {templates.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : list.length === 0 || tab === 'templates' ? (
        <TemplatesTab templates={list} />
      ) : tab === 'cut' ? (
        <CarcassesTab templates={list} />
      ) : tab === 'history' ? (
        <HistoryTab />
      ) : (
        <YieldsTab />
      )}
    </>
  );
}

// ---------------------------------------------------------------- medias reses para despostar
function CarcassesTab({ templates }: { templates: CutTemplateDTO[] }) {
  const lots = useQuery({ queryKey: ['cutting', 'carcasses'], queryFn: () => api.get<CarcassLotDTO[]>('/cutting/carcasses') });
  const [cutting, setCutting] = useState<CarcassLotDTO | null>(null);
  const [done, setDone] = useState<CuttingRunDTO | null>(null);
  const rows = lots.data ?? [];
  return (
    <>
      <Card>
        <CardHeader
          title="Medias reses en stock"
          subtitle={
            <>
              Entran cargando la compra en <Link to="/compras/nueva">Compras</Link> con el producto “
              {templates.map((t) => t.sourceProductName).join('” o “')}”.
            </>
          }
        />
        <CardBody flush>
          {lots.isPending ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<Beef />} title="No hay medias reses para despostar" text="Cuando cargues la compra de una media res, aparece acá." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.lotId}>
                      <td>
                        <span className="bf-cell-main">{l.productName}</span>
                        <div className="bf-cell-sub">
                          {[l.supplierName, `entró el ${formatDate(l.receivedAt)}`, l.branchName].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatKilos(l.qty)}
                        {l.unitCostCents !== null && <div className="bf-cell-sub">{formatMoney(l.unitCostCents)}/kg</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Button variant="primary" size="sm" icon={<Scale />} onClick={() => setCutting(l)}>
                          Despostar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {cutting && (
        <CutDialog
          lot={cutting}
          templates={templates}
          onClose={() => setCutting(null)}
          onDone={(r) => {
            setCutting(null);
            setDone(r);
          }}
        />
      )}
      {done && <RunDialog run={done} onClose={() => setDone(null)} justCreated />}
    </>
  );
}

interface CutLine {
  templateLineId: string;
  kind: 'CUT' | 'TACHO';
  productId: string | null;
  label: string | null;
  name: string;
  percent: number;
}
interface Weight {
  value: string;
  skipped: boolean;
  /** Planilla manual: el % que escribe la carnicería. */
  pct?: string;
}

/** Un peso está "raro" si se aleja mucho de lo que suele dar ese corte (para detectar un 118 en vez de 11,8). */
/** Corte nuevo para sumar: se elige uno que ya existe o se escribe el nombre y se crea solo. */
interface NewCut {
  kind: 'CUT' | 'TACHO';
  productId: string | null;
  label: string | null;
  name: string;
}
function AddCutForm({
  animal,
  exclude,
  allowTacho,
  hint,
  onAdd,
  children,
}: {
  animal: CarcassAnimal;
  /** Nombres que ya están (no se repiten). */
  exclude: string[];
  allowTacho?: boolean;
  hint?: string;
  onAdd: (c: NewCut) => void;
  children?: ReactNode;
}) {
  const products = useProducts();
  const [name, setName] = useState('');
  const [tacho, setTacho] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taken = new Set(exclude.map((x) => x.toLowerCase()));
  const options = (products.data ?? []).filter((p) => p.kind === 'MEAT' && !p.isCarcass && !taken.has(p.name.toLowerCase()));
  const listId = `cuts-${animal}`;
  async function add() {
    const n = name.trim();
    if (n.length < 2) return setError('Escribí el nombre del corte.');
    if (taken.has(n.toLowerCase())) return setError('Ese corte ya está en la lista.');
    setBusy(true);
    setError(null);
    try {
      if (tacho) onAdd({ kind: 'TACHO', productId: null, label: n, name: n });
      else {
        const p = await api.post<{ id: string; name: string }>('/cutting/cut-products', { name: n, animal });
        onAdd({ kind: 'CUT', productId: p.id, label: null, name: p.name });
      }
      setName('');
      setTacho(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="bf-add-cut">
      <div className="bf-add-cut-row">
        <Input
          list={listId}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="Ej.: Entraña"
          aria-label="Nombre del corte"
        />
        <datalist id={listId}>
          {options.map((p) => (
            <option key={p.id} value={p.name} />
          ))}
        </datalist>
        <Button icon={<Plus />} loading={busy} onClick={() => void add()}>
          Agregar
        </Button>
      </div>
      {allowTacho && (
        <label className="bf-check bf-cell-sub">
          <input type="checkbox" checked={tacho} onChange={(e) => setTacho(e.target.checked)} /> Va al tacho (hueso, grasa: no se vende)
        </label>
      )}
      {hint && <span className="bf-cell-sub">{hint}</span>}
      {children}
      {error && <p className="bf-field-error">{error}</p>}
    </div>
  );
}

function looksOff(real: number, ref: number): boolean {
  if (ref < 300 || real <= 0) return false;
  return real > ref * 2 || real < ref * 0.4;
}

/**
 * Desposte: todo se pesa. La referencia en gris (lo que suele dar cada corte) solo sirve para
 * avisar si un peso parece mal escrito; nunca se usa para calcular.
 */
function CutDialog({
  lot,
  templates,
  onClose,
  onDone,
}: {
  lot: CarcassLotDTO;
  templates: CutTemplateDTO[];
  onClose: () => void;
  onDone: (r: CuttingRunDTO) => void;
}) {
  const qc = useQueryClient();
  const { me } = useSession();
  // Sin el adicional: planilla manual (sin balanza, sin referencias ni cálculos).
  const auto = me.tenant.modules.includes('CUTTING');
  const [manualYield, setManualYield] = useState('');
  const workers = useQuery({ queryKey: ['workers'], queryFn: () => api.get<ExternalWorkerDTO[]>('/workers') });
  const activeWorkers = (workers.data ?? []).filter((w) => w.isActive);
  const tpl = templates.find((t) => t.sourceProductId === lot.productId);
  // Predicción del motor de aprendizaje: lo que suele dar la media res de ESTE frigorífico.
  const forecast = useQuery({
    queryKey: ['cutting', 'forecast', tpl?.id, lot.supplierId],
    queryFn: () => api.get<CuttingForecastDTO>(`/cutting/forecast?templateId=${tpl!.id}${lot.supplierId ? `&supplierId=${lot.supplierId}` : ''}`),
    enabled: auto && !!tpl,
    staleTime: 60_000,
  });
  const fc = forecast.data && forecast.data.samples > 0 ? forecast.data : null;
  const [kg, setKg] = useState(fromInternalQty('WEIGHT', lot.qty));
  const [workerId, setWorkerId] = useState('');
  const [notes, setNotes] = useState('');
  const [weights, setWeights] = useState<Record<string, Weight>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const source = toInternalQty('WEIGHT', kg || '0') ?? 0;

  // Cortes que no están en la lista y se agregan en este desposte (ej.: una entraña).
  const [extras, setExtras] = useState<CutLine[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [keepExtras, setKeepExtras] = useState(true);
  const lines: CutLine[] = useMemo(
    () => [
      ...(tpl?.lines ?? []).map((l) => ({
        templateLineId: l.id,
        kind: l.kind,
        productId: l.productId,
        label: l.label,
        name: l.productName ?? l.label ?? '',
        percent: fc?.lines.find((x) => x.templateLineId === l.id)?.percent ?? l.percent,
      })),
      ...extras,
    ],
    [tpl, extras, fc],
  );
  const isExtra = (l: CutLine) => l.templateLineId.startsWith('extra:');
  const w = (id: string): Weight => weights[id] ?? { value: '', skipped: false };
  const ref = (l: CutLine) => Math.round((l.percent / 100) * source);
  const num = (v: string | undefined) => {
    const n = Number((v ?? '').replace(',', '.'));
    return v && v.trim() && Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  };
  const qtyOf = (l: CutLine) => (w(l.templateLineId).skipped ? 0 : (toInternalQty('WEIGHT', w(l.templateLineId).value || '0') ?? 0));
  const missing = lines.filter((l) => !w(l.templateLineId).skipped && qtyOf(l) <= 0);
  const cuts = lines.filter((l) => l.kind === 'CUT').reduce((a, l) => a + qtyOf(l), 0);
  const tacho = lines.filter((l) => l.kind === 'TACHO').reduce((a, l) => a + qtyOf(l), 0);
  const loss = source - cuts - tacho;
  const unpriced = (tpl?.lines ?? []).filter((l) => l.kind === 'CUT' && !l.priceCents).length;
  const set = (id: string, patch: Partial<Weight>) => setWeights((ws) => ({ ...ws, [id]: { ...w(id), ...patch } }));

  // Balanza conectada a esta computadora: el peso de cada corte entra con un toque.
  const hw = useHardware();
  const scale = useScale();
  const useScaleHere = auto && hw.scale.enabled && scale.status !== 'unsupported';
  const [, tick] = useState(0);
  useEffect(() => {
    if (!useScaleHere) return;
    void connectScale();
    const t = setInterval(() => tick((n) => n + 1), 400);
    return () => clearInterval(t);
  }, [useScaleHere]);
  const live = scaleFresh(scale);
  const liveGrams = live ? Math.max(0, scale.grams ?? 0) : 0;
  // Después de anotar, hay que sacar el corte de la balanza: así no se anota dos veces el mismo.
  const [armed, setArmed] = useState(true);
  useEffect(() => {
    if (!armed && live && liveGrams < 50) setArmed(true);
  }, [armed, live, liveGrams]);
  const canTake = armed && live && scale.stable && liveGrams > 0;
  const nextLine = lines.find((l) => !w(l.templateLineId).skipped && !w(l.templateLineId).value);
  const take = (id: string) => {
    if (!canTake) return;
    set(id, { value: fromInternalQty('WEIGHT', liveGrams), skipped: false });
    setArmed(false);
  };

  if (!tpl) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()} title={`Despostar ${lot.productName}`}>
        <Callout tone="warning">
          “{lot.productName}” no tiene lista de cortes. Creala en la pestaña <strong>Cortes</strong> con ese mismo nombre.
        </Callout>
      </Dialog>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!tpl || source <= 0) return setError('Revisá los kilos de la media res.');
    if (source > lot.qty) return setError(`De esa media res quedan ${formatKilos(lot.qty)}.`);
    if (missing.length) return setError(`Falta pesar: ${missing.map((l) => l.name).join(', ')}. Si alguno no lo hiciste, marcá “No lo hice”.`);
    if (loss < 0) return setError('Los cortes y el tacho pesan más que la media res. Revisá los pesos.');
    setBusy(true);
    setError(null);
    try {
      const run = await api.post<CuttingRunDTO>('/cutting/runs', {
        templateId: tpl.id,
        sourceLotId: lot.lotId,
        sourceQty: source,
        workerId: workerId || null,
        notes: notes.trim() || null,
        lines: lines.map((l) => ({
          templateLineId: isExtra(l) ? null : l.templateLineId,
          kind: l.kind,
          productId: l.productId,
          label: l.label,
          estimatedQty: auto ? ref(l) : 0,
          qty: qtyOf(l),
          weighed: !w(l.templateLineId).skipped,
          manualPercent: auto ? null : num(w(l.templateLineId).pct),
        })),
        manualYield: auto ? null : num(manualYield),
      });
      // Los cortes agregados se suman a la lista para la próxima (con el porcentaje de este desposte como referencia).
      const toKeep = extras.filter((x) => keepExtras && qtyOf(x) > 0);
      if (toKeep.length) {
        try {
          await api.put(`/cutting/templates/${tpl.id}`, {
            animal: tpl.animal,
            name: tpl.name,
            lines: [
              ...tpl.lines.map((l) => ({ kind: l.kind, productId: l.productId, label: l.label, percent: l.defaultPercent })),
              ...toKeep.map((x) => ({
                kind: x.kind,
                productId: x.productId,
                label: x.label,
                percent: Math.round((qtyOf(x) / source) * 10000) / 100,
              })),
            ],
          });
        } catch {
          /* el desposte ya quedó; la lista se puede editar después */
        }
      }
      await Promise.all(['cutting', 'stock', 'products', 'pos', 'waste', 'workers', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onDone(run);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Enter pasa al peso siguiente (se carga rápido con el teclado numérico).
  function nextOnEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('[data-cut-weight]:not(:disabled)'));
    const i = inputs.indexOf(e.currentTarget);
    inputs[i + 1]?.focus();
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Despostar ${lot.productName}`}
      description={
        !auto
          ? 'Planilla a mano: escribí los kilos que pesaste de cada corte y, si querés, el % que sacás con tu fórmula.'
          : useScaleHere
            ? 'Poné cada corte en la balanza y tocá “Anotar”: el peso entra solo. En gris, lo que suele dar en tu carnicería.'
            : 'Pesá cada corte y escribí los kilos. En gris, lo que suele dar en tu carnicería: sirve para darte cuenta si un peso quedó mal escrito.'
      }
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {fc && (
          <div className="bf-reco bf-reco--soft">
            <BrainCircuit aria-hidden />
            <div>
              Referencias según <strong>{fc.supplierName ?? 'este frigorífico'}</strong>: aprendidas de {fc.samples} desposte
              {fc.samples === 1 ? '' : 's'}
              {fc.expectedYield !== null && <> · rinde esperado {pts(fc.expectedYield)} %</>}.
            </div>
          </div>
        )}
        {auto && unpriced > 0 && (
          <Callout tone="warning">
            {unpriced} {unpriced === 1 ? 'corte no tiene' : 'cortes no tienen'} precio de venta: para repartir el costo cuentan al precio promedio.
            Cargalos en <Link to="/precios">Precios</Link> para que la ganancia por corte sea exacta.
          </Callout>
        )}
        <div className="bf-form-row">
          <Field label="Kilos de la media res" hint={`Según la compra: ${formatKilos(lot.qty)}.`}>
            <Input className="bf-input--lg" inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} />
          </Field>
          {activeWorkers.length > 0 && (
            <Field label="¿Lo desposta el despostero?" hint="Su costo se suma a los cortes y se le anota el trabajo.">
              <Select value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
                <option value="">No, lo hacemos nosotros</option>
                {activeWorkers.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        {useScaleHere && (
          <div className="bf-cut-scale" data-state={canTake ? 'ok' : live ? 'moving' : 'off'}>
            <div className="bf-cut-scale-read">
              <Scale aria-hidden />
              <div>
                <strong className="bf-num">{live ? formatKilos(liveGrams) : '—'}</strong>
                <span>
                  {!armed && live ? (
                    'Anotado. Sacá el corte y poné el siguiente.'
                  ) : canTake ? (
                    'Peso estable'
                  ) : live ? (
                    liveGrams > 0 ? (
                      'Pesando…'
                    ) : (
                      'Poné el corte en la balanza'
                    )
                  ) : (
                    <>
                      La balanza no manda el peso.{' '}
                      <button type="button" className="bf-link-btn" onClick={() => void connectScale(scale.status !== 'connected')}>
                        Conectar
                      </button>
                    </>
                  )}
                </span>
              </div>
            </div>
            {nextLine ? (
              <Button variant="primary" disabled={!canTake} onClick={() => take(nextLine.templateLineId)}>
                Anotar en {nextLine.name}
              </Button>
            ) : (
              <span className="bf-cell-sub">Todo pesado</span>
            )}
          </div>
        )}
        <div className="bf-scroll-box bf-cut-table">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Corte</th>
                <th style={{ textAlign: 'right' }}>{auto ? 'Peso real' : 'Kilos'}</th>
                {!auto && <th style={{ textAlign: 'right' }}>% (tu fórmula)</th>}
                <th style={{ textAlign: 'right' }}>No lo hice</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const cur = w(l.templateLineId);
                const q = qtyOf(l);
                const off = auto && !cur.skipped && looksOff(q, ref(l));
                return (
                  <tr key={l.templateLineId} style={{ opacity: cur.skipped ? 0.45 : 1 }}>
                    <td>
                      <span className="bf-cell-main">{l.name}</span> {l.kind === 'TACHO' && <Badge>al tacho</Badge>}{' '}
                      {isExtra(l) && <Badge tone="info">agregado</Badge>}
                      <div className="bf-cell-sub">
                        {auto && isExtra(l) && ref(l) > 0 && <>suele dar {formatKilos(ref(l))} · </>}
                        {isExtra(l) ? (
                          <button
                            type="button"
                            className="bf-link-btn"
                            onClick={() => {
                              setExtras((xs) => xs.filter((x) => x.templateLineId !== l.templateLineId));
                              setWeights(({ [l.templateLineId]: _drop, ...rest }) => rest);
                            }}
                          >
                            Quitar
                          </button>
                        ) : auto ? (
                          `suele dar ${formatKilos(ref(l))}`
                        ) : null}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Input
                        data-cut-weight
                        inputMode="decimal"
                        value={cur.value}
                        disabled={cur.skipped}
                        onChange={(e) => set(l.templateLineId, { value: e.target.value })}
                        onKeyDown={nextOnEnter}
                        placeholder="kg"
                        aria-label={`Kilos de ${l.name}`}
                        aria-invalid={off || undefined}
                        className={off ? 'bf-input--warn' : undefined}
                        style={{ maxWidth: 110, marginLeft: 'auto' }}
                      />
                      {useScaleHere && !cur.skipped && (
                        <div>
                          <button type="button" className="bf-link-btn bf-cell-sub" disabled={!canTake} onClick={() => take(l.templateLineId)}>
                            Tomar peso
                          </button>
                        </div>
                      )}
                      {off && <div className="bf-text-warning bf-cell-sub">¿Está bien?</div>}
                    </td>
                    {!auto && (
                      <td style={{ textAlign: 'right' }}>
                        <Input
                          inputMode="decimal"
                          value={cur.pct ?? ''}
                          disabled={cur.skipped}
                          onChange={(e) => set(l.templateLineId, { pct: e.target.value })}
                          placeholder="%"
                          aria-label={`Porcentaje de ${l.name}`}
                          style={{ maxWidth: 80, marginLeft: 'auto' }}
                        />
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="checkbox"
                        checked={cur.skipped}
                        onChange={(e) => set(l.templateLineId, { skipped: e.target.checked, value: '' })}
                        aria-label={`${l.name}: no lo hice`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {addOpen ? (
          <AddCutForm
            animal={tpl.animal}
            exclude={lines.map((l) => l.name)}
            onAdd={(c) => {
              setExtras((xs) => [
                ...xs,
                { ...c, templateLineId: `extra:${c.productId ?? c.name}`, percent: standardCutPercent(tpl.animal, c.name) ?? 0 },
              ]);
              setAddOpen(false);
            }}
          >
            <label className="bf-check bf-cell-sub">
              <input type="checkbox" checked={keepExtras} onChange={(e) => setKeepExtras(e.target.checked)} /> Sumar los cortes agregados a la lista
              de {tpl.name} para la próxima
            </label>
          </AddCutForm>
        ) : (
          <div>
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setAddOpen(true)}>
              Agregar otro corte
            </Button>
          </div>
        )}
        {!auto && (
          <Field label="Rinde (%)" hint="El que calculás con tu planilla. Opcional: el sistema no lo calcula.">
            <Input
              inputMode="decimal"
              value={manualYield}
              onChange={(e) => setManualYield(e.target.value)}
              placeholder="%"
              style={{ maxWidth: 140 }}
            />
          </Field>
        )}
        {auto && (
          <div className="bf-close-summary">
            <div>
              <span>Cortes</span>
              <strong className="bf-num">
                {formatKilos(cuts)} · {source > 0 ? pct(cuts / source) : '—'}
              </strong>
            </div>
            <div>
              <span>Al tacho (hueso, grasa)</span>
              <strong className="bf-num">{formatKilos(tacho)}</strong>
            </div>
            <div>
              <span>{loss >= 0 ? 'Se pierde al despostar (oreo, sangre, aserrín)' : 'Pesan más que la media res'}</span>
              <strong className={`bf-num ${loss < 0 ? 'bf-text-danger' : ''}`}>{missing.length ? '—' : formatKilos(Math.abs(loss))}</strong>
            </div>
          </div>
        )}
        <Field label="Notas (opcional)">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <span className={missing.length ? 'bf-text-warning' : 'bf-cell-sub'} style={{ marginRight: 'auto' }}>
            {missing.length ? `Faltan ${missing.length}` : auto ? 'Todo pesado' : 'Planilla completa'}
          </span>
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={<Scale />} loading={busy} disabled={source <= 0}>
            {auto ? 'Confirmar desposte' : 'Cargar cortes al stock'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- historial
function HistoryTab() {
  const runs = useQuery({ queryKey: ['cutting', 'runs'], queryFn: () => api.get<CuttingRunDTO[]>('/cutting/runs') });
  const [open, setOpen] = useState<CuttingRunDTO | null>(null);
  const rows = runs.data ?? [];
  return (
    <Card>
      <CardBody flush>
        {runs.isPending ? (
          <div style={{ padding: 16 }}>
            <Skeleton />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<History />} title="Todavía no hay despostes" />
        ) : (
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Media res</th>
                  <th style={{ textAlign: 'right' }}>Kilos</th>
                  <th style={{ textAlign: 'right' }}>Rinde</th>
                  <th style={{ textAlign: 'right' }}>Tacho</th>
                  <th style={{ textAlign: 'right' }}>Se pierde</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-clickable="true" onClick={() => setOpen(r)} style={{ opacity: r.status === 'VOIDED' ? 0.5 : 1 }}>
                    <td className="bf-cell-sub">{formatDateTime(r.createdAt)}</td>
                    <td>
                      <span className="bf-cell-main">{r.sourceProductName}</span> {r.status === 'VOIDED' && <Badge>Anulado</Badge>}
                      <div className="bf-cell-sub">{[r.supplierName, r.workerName && `despostó ${r.workerName}`].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {formatKilos(r.sourceQty)}
                    </td>
                    <td className={`bf-num ${lowYield(r) ? 'bf-text-warning' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                      {r.mode === 'MANUAL' ? (r.manualYield !== null ? `${r.manualYield.toLocaleString('es-AR')} %` : '—') : pct(r.yieldRatio)}
                      {r.mode === 'MANUAL' && <div className="bf-cell-sub">planilla</div>}
                      {r.typicalYield !== null && <div className="bf-cell-sub">normal {pct(r.typicalYield)}</div>}
                    </td>
                    <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                      {formatKilos(r.tachoQty)}
                    </td>
                    <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                      {r.mode === 'MANUAL' ? '—' : formatKilos(r.lossQty)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      {open && <RunDialog run={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}

/** Rinde 2 puntos o más por debajo de lo normal para ese tipo de media res. */
const lowYield = (r: CuttingRunDTO) => r.status === 'DONE' && r.typicalYield !== null && r.yieldRatio < r.typicalYield - 0.02;

function RunDialog({ run, onClose, justCreated }: { run: CuttingRunDTO; onClose: () => void; justCreated?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const showCost = run.sourceCostCents !== null;
  const manual = run.mode === 'MANUAL';
  const showCalc = showCost && !manual;
  // Pasar el precio sugerido a Productos: se tildan por defecto los cortes que hoy se venden por debajo del sugerido.
  const access = useCatalogAccess();
  const canUpdate = showCost && access.canPrices && run.status !== 'VOIDED';
  const priceable = run.lines.filter(
    (l) => l.kind === 'CUT' && l.qty > 0 && l.productId && l.suggestedPriceCents && l.suggestedPriceCents !== l.priceCents,
  );
  const [pick, setPick] = useState<Set<string>>(
    () => new Set(priceable.filter((l) => !l.priceCents || l.priceCents < (l.suggestedPriceCents ?? 0)).map((l) => l.productId!)),
  );
  const [pricing, setPricing] = useState(false);
  async function applyPrices() {
    setPricing(true);
    try {
      const branchId = access.canGeneralPrice ? null : access.priceBranchId;
      for (const l of priceable.filter((x) => pick.has(x.productId!))) {
        await api.put(`/products/${l.productId}/price`, { priceCents: l.suggestedPriceCents, branchId });
      }
      await Promise.all(['products', 'pos', 'cutting'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success(
        'Precios actualizados',
        `${pick.size} ${pick.size === 1 ? 'corte' : 'cortes'} con el precio sugerido. Quedan en el historial de precios.`,
      );
      setPick(new Set());
    } catch (err) {
      toast.error('No se pudieron actualizar todos', errorMessage(err));
    } finally {
      setPricing(false);
    }
  }

  async function doVoid() {
    const parsed = voidReasonSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    try {
      await api.post(`/cutting/runs/${run.id}/void`, parsed.data);
      await Promise.all(['cutting', 'stock', 'workers'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Desposte anulado', 'La media res volvió al stock.');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={
        justCreated
          ? manual
            ? 'Listo: los cortes entraron al stock'
            : `Listo: rinde ${pct(run.yieldRatio)}`
          : `${run.sourceProductName} · ${formatDateTime(run.createdAt)}`
      }
      description={[run.supplierName, formatKilos(run.sourceQty), run.workerName && `despostó ${run.workerName}`].filter(Boolean).join(' · ')}
      wide
    >
      <div className="bf-form">
        {run.status === 'VOIDED' && <Callout tone="danger">Anulado: {run.voidReason}</Callout>}
        {lowYield(run) && (
          <Callout tone="warning">
            Rindió menos de lo normal: {pct(run.yieldRatio)} contra {pct(run.typicalYield ?? 0)} de tus últimos despostes de{' '}
            {run.sourceProductName.toLowerCase()}.{run.supplierName ? ` Para reclamarle a ${run.supplierName}.` : ''}
          </Callout>
        )}
        <div className="bf-close-summary">
          <div>
            <span>Cortes</span>
            <strong className="bf-num">
              {formatKilos(run.cutsQty)}
              {manual ? '' : ` · ${pct(run.yieldRatio)}`}
            </strong>
          </div>
          <div>
            <span>Al tacho</span>
            <strong className="bf-num">{formatKilos(run.tachoQty)}</strong>
          </div>
          {manual ? (
            <div>
              <span>Rinde (tu planilla)</span>
              <strong className="bf-num">{run.manualYield !== null ? `${run.manualYield.toLocaleString('es-AR')} %` : '—'}</strong>
            </div>
          ) : (
            <div>
              <span>Se pierde al despostar</span>
              <strong className="bf-num">{formatKilos(run.lossQty)}</strong>
            </div>
          )}
        </div>
        {showCost && (
          <p className="bf-cell-sub" style={{ margin: 0 }}>
            Costo de la media res {formatMoney(run.sourceCostCents ?? 0)}
            {run.workerCostCents ? ` + despostero ${formatMoney(run.workerCostCents)}` : ''},{' '}
            {manual ? 'repartido parejo por kilo entre los cortes.' : 'repartido entre los cortes según lo que vale cada uno.'}
          </p>
        )}
        <div className="bf-scroll-box bf-cut-table">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Corte</th>
                <th style={{ textAlign: 'right' }}>Kilos</th>
                <th style={{ textAlign: 'right' }}>{manual ? '%' : 'Rinde'}</th>
                {showCost && <th style={{ textAlign: 'right' }}>Costo/kg</th>}
                <th style={{ textAlign: 'right' }}>Precio</th>
                {showCalc && <th style={{ textAlign: 'right' }}>Ganancia</th>}
                {showCalc && <th style={{ textAlign: 'right' }}>Precio sugerido</th>}
                {canUpdate && priceable.length > 0 && (
                  <th style={{ textAlign: 'right' }} title="Actualizar el precio">
                    ✓
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {run.lines
                .filter((l) => l.qty > 0)
                .map((l, i) => (
                  <tr key={i}>
                    <td>
                      <span className="bf-cell-main">{l.productName ?? l.label}</span> {l.kind === 'TACHO' && <Badge>al tacho</Badge>}
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {formatKilos(l.qty)}
                    </td>
                    <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                      {manual
                        ? l.manualPercent !== null
                          ? `${l.manualPercent.toLocaleString('es-AR')} %`
                          : ''
                        : run.sourceQty > 0
                          ? pct(l.qty / run.sourceQty)
                          : ''}
                    </td>
                    {showCost && (
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {l.unitCostCents !== null ? formatMoney(l.unitCostCents) : ''}
                      </td>
                    )}
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {l.kind === 'CUT' ? l.priceCents ? formatMoney(l.priceCents) : <Badge tone="warning">sin precio</Badge> : ''}
                    </td>
                    {showCalc && (
                      <td
                        className={`bf-num ${l.marginRatio !== null && l.marginRatio < 0.15 ? 'bf-text-danger' : ''}`}
                        style={{ textAlign: 'right' }}
                      >
                        {l.marginRatio !== null ? pct(l.marginRatio) : ''}
                      </td>
                    )}
                    {showCalc && (
                      <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                        {l.suggestedPriceCents ? formatMoney(l.suggestedPriceCents) : ''}
                      </td>
                    )}
                    {canUpdate && priceable.length > 0 && (
                      <td style={{ textAlign: 'right' }}>
                        {l.productId && priceable.includes(l) && (
                          <input
                            type="checkbox"
                            checked={pick.has(l.productId)}
                            aria-label={`Actualizar el precio de ${l.productName}`}
                            onChange={(e) =>
                              setPick((s) => {
                                const n = new Set(s);
                                if (e.target.checked) n.add(l.productId!);
                                else n.delete(l.productId!);
                                return n;
                              })
                            }
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {showCalc && (
          <p className="bf-cell-sub" style={{ margin: 0 }}>
            El precio sugerido mantiene la ganancia objetivo (Configuración → Equipo). Rinde = kilos del corte ÷ kilos de la media res.
          </p>
        )}
        {canUpdate && priceable.length > 0 && (
          <div className="bf-form-actions" style={{ justifyContent: 'flex-start' }}>
            <Button icon={<Tag />} loading={pricing} disabled={pick.size === 0} onClick={() => void applyPrices()}>
              Actualizar precios ({pick.size})
            </Button>
            <span className="bf-cell-sub">Pasa el precio sugerido a los cortes tildados. Queda en el historial de precios.</span>
          </div>
        )}
        {voiding ? (
          <>
            <Field label="Motivo" error={error ?? undefined}>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </Field>
            <div className="bf-form-actions">
              <Button onClick={() => setVoiding(false)} disabled={busy}>
                Volver
              </Button>
              <Button variant="danger" loading={busy} onClick={() => void doVoid()}>
                Anular desposte
              </Button>
            </div>
          </>
        ) : (
          <div className="bf-form-actions">
            {run.canVoid && (
              <Button variant="ghost" onClick={() => setVoiding(true)} style={{ marginRight: 'auto' }}>
                Anular
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>
              Listo
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- frigoríficos (motor de aprendizaje)
const pts = (r: number) => (r * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 });

/** Mini gráfico del rinde de los últimos despostes (los descartados en gris). */
function YieldSpark({ history }: { history: SupplierInsightDTO['history'] }) {
  if (history.length < 2) return null;
  const ys = history.map((h) => h.yield);
  const min = Math.min(...ys) - 0.01;
  const max = Math.max(...ys) + 0.01;
  const W = 96;
  const H = 26;
  const x = (i: number) => (i / (history.length - 1)) * (W - 4) + 2;
  const y = (v: number) => H - 2 - ((v - min) / (max - min || 1)) * (H - 4);
  const good = history.map((h, i) => ({ ...h, i })).filter((h) => !h.excluded);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Rinde de los últimos despostes" className="bf-spark">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.6" points={good.map((h) => `${x(h.i)},${y(h.yield)}`).join(' ')} />
      {history.map((h, i) => (
        <circle key={i} cx={x(i)} cy={y(h.yield)} r={h.excluded ? 2.4 : 1.8} fill={h.excluded ? 'var(--bf-faint)' : 'currentColor'} />
      ))}
    </svg>
  );
}

function ConfidenceBadge({ c }: { c: SupplierInsightDTO['confidence'] }) {
  return <Badge tone={c === 'HIGH' ? 'success' : c === 'MEDIUM' ? 'info' : 'neutral'}>{INSIGHT_CONFIDENCE_LABELS[c]}</Badge>;
}

function YieldsTab() {
  const q = useQuery({ queryKey: ['cutting', 'insights'], queryFn: () => api.get<CuttingInsightsDTO>('/cutting/insights') });
  const [pick, setPick] = useState('');
  const list = q.data?.carcasses ?? [];
  const c = list.find((x) => x.productId === pick) ?? list[0];
  if (q.isPending) return <Skeleton style={{ height: 240 }} />;
  if (!c)
    return (
      <EmptyState
        icon={<BrainCircuit />}
        title="Todavía no hay despostes para aprender"
        text="Con cada media res que despostes pesando los cortes, BifeAI aprende cuánto rinde cada frigorífico y cuál te conviene."
      />
    );
  return (
    <div className="bf-form" style={{ gap: 16 }}>
      {list.length > 1 && (
        <Field label="Tipo de media res" hint="Cada tipo se compara por separado: no se mezcla novillo con vaca.">
          <Select value={c.productId} onChange={(e) => setPick(e.target.value)} style={{ maxWidth: 320 }}>
            {list.map((x) => (
              <option key={x.productId} value={x.productId}>
                {x.productName} · {x.runs} despostes
              </option>
            ))}
          </Select>
        </Field>
      )}
      <InsightSummary c={c} byMoney={!!q.data?.byMoney} />
      <SupplierRanking c={c} byMoney={!!q.data?.byMoney} />
      <CutsComparison c={c} />
      <Card>
        <CardHeader title="Cómo aprende" />
        <CardBody>
          <ul className="bf-plain-list">
            <li>
              <strong>Cada desposte pesado le enseña</strong> cuánto rinde la media res de ese frigorífico y cuánto sale de cada corte. Lo reciente
              pesa más que lo viejo (el ganado y los frigoríficos cambian).
            </li>
            <li>
              <strong>No se deja engañar por un solo desposte:</strong> con pocos datos se apoya en el promedio de todos; a medida que suma despostes,
              manda la historia de cada frigorífico.
            </li>
            <li>
              <strong>Descarta los pesos raros</strong> (un desposte que da muy distinto a lo normal de ese frigorífico) para no aprender de un error.
            </li>
            <li>
              <strong>Compara en plata:</strong> lo que valen los cortes a tus precios de hoy contra el último precio que le pagaste a cada uno.
              Recién recomienda cuando está seguro en un 80 % o más.
            </li>
            <li>
              <strong>Vigila los cambios:</strong> si un frigorífico empieza a rendir menos que lo que venía dando, te avisa.
            </li>
            <li>
              Al despostar, los pesos de referencia ya salen <strong>según el frigorífico</strong> de esa media res.
            </li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}

function InsightSummary({ c, byMoney }: { c: CarcassInsightDTO; byMoney: boolean }) {
  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <BrainCircuit size={20} aria-hidden /> Qué frigorífico te conviene
          </span>
        }
        subtitle={`${c.productName} · aprendido de ${c.runs} desposte${c.runs === 1 ? '' : 's'}${
          c.excluded ? ` (${c.excluded} descartado${c.excluded === 1 ? '' : 's'} por pesos raros)` : ''
        }${byMoney ? '' : ' · comparado por rinde (sin permiso para ver costos)'}`}
      />
      <CardBody>
        <div className="bf-form" style={{ gap: 12 }}>
          {c.recommendation ? (
            <div className="bf-reco">
              <Sparkles aria-hidden />
              <div>
                <strong>{c.recommendation.text}</strong>
                <div className="bf-cell-sub">
                  {Math.round(c.recommendation.certainty * 100)} % de seguridad · rinde {pts(c.recommendation.yieldGap / 100)} puntos{' '}
                  {c.recommendation.yieldGap >= 0 ? 'más' : 'menos'} que {c.recommendation.versusName}
                </div>
              </div>
            </div>
          ) : (
            <Callout>{c.pending}</Callout>
          )}
          {c.alerts.map((a) => (
            <Callout key={a.text} tone={a.kind === 'DROP' ? 'warning' : 'success'}>
              {a.text}
            </Callout>
          ))}
          {c.highlights.filter((h) => h !== c.recommendation?.text && !c.alerts.some((a) => a.text === h)).length > 0 && (
            <ul className="bf-plain-list">
              {c.highlights
                .filter((h) => h !== c.recommendation?.text && !c.alerts.some((a) => a.text === h))
                .map((h) => (
                  <li key={h}>{h}</li>
                ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function SupplierRanking({ c, byMoney }: { c: CarcassInsightDTO; byMoney: boolean }) {
  return (
    <Card>
      <CardHeader
        title="Ranking de frigoríficos"
        subtitle={
          byMoney ? 'Ordenados por lo que te dejan por cada peso que les pagás, a tus precios de venta de hoy.' : 'Ordenados por rinde aprendido.'
        }
      />
      <CardBody flush>
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Frigorífico</th>
                <th className="bf-num">Rinde</th>
                <th className="bf-num">Merma</th>
                <th className="bf-num">Regularidad</th>
                <th>Últimos despostes</th>
                {byMoney && (
                  <>
                    <th className="bf-num">Vale 1 kg</th>
                    <th className="bf-num">Le pagás</th>
                    <th className="bf-num">Te deja por kg</th>
                    <th className="bf-num">Costo por kg vendible</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {c.suppliers.map((s) => (
                <tr key={s.supplierId ?? 'none'}>
                  <td className="bf-num">{s.rank}</td>
                  <td>
                    <span className="bf-cell-main">{s.supplierName}</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
                      <ConfidenceBadge c={s.confidence} />
                      <span className="bf-cell-sub">
                        {s.runs} desposte{s.runs === 1 ? '' : 's'}
                        {s.excluded ? ` · ${s.excluded} descartado${s.excluded === 1 ? '' : 's'}` : ''}
                      </span>
                    </div>
                  </td>
                  <td className="bf-num" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {pts(s.yield)} %
                    {s.trend !== null && Math.abs(s.trend) >= 1 && (
                      <div className={`bf-cell-sub ${s.trend < 0 ? 'bf-text-danger' : 'bf-text-success'}`}>
                        {s.trend < 0 ? <TrendingDown size={13} aria-hidden /> : <TrendingUp size={13} aria-hidden />} {s.trend > 0 ? '+' : ''}
                        {s.trend.toLocaleString('es-AR', { maximumFractionDigits: 1 })} pts
                      </div>
                    )}
                  </td>
                  <td className="bf-num bf-cell-sub">{pts(s.loss)} %</td>
                  <td className="bf-num bf-cell-sub">
                    {s.spread !== null ? `±${s.spread.toLocaleString('es-AR', { maximumFractionDigits: 1 })} pts` : '—'}
                  </td>
                  <td style={{ color: 'var(--bf-celeste)' }}>
                    <YieldSpark history={s.history} />
                  </td>
                  {byMoney && (
                    <>
                      <td className="bf-num">{s.valuePerKgCents !== null ? formatMoney(s.valuePerKgCents) : '—'}</td>
                      <td className="bf-num">
                        {s.costPerKgCents !== null ? formatMoney(s.costPerKgCents) : '—'}
                        {s.costAt && <div className="bf-cell-sub">{formatDate(s.costAt)}</div>}
                      </td>
                      <td className="bf-num" style={{ fontWeight: 600 }}>
                        {s.marginPerKgCents !== null ? formatMoney(s.marginPerKgCents) : '—'}
                      </td>
                      <td className="bf-num">{s.costPerSellableKgCents !== null ? formatMoney(s.costPerSellableKgCents) : '—'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
      <CardBody>
        <p className="bf-hint" style={{ margin: 0 }}>
          <strong>Rinde</strong>: kilos de cortes para vender por cada 100 kg de media res. <strong>Regularidad</strong>: cuánto varía de una media
          res a otra (menos es más parejo).
          {byMoney && (
            <>
              {' '}
              <strong>Vale 1 kg</strong>: lo que vendés de los cortes de 1 kg de media res, a tus precios de hoy. <strong>Le pagás</strong>: el último
              precio por kilo de su factura. <strong>Costo por kg vendible</strong>: lo que te cuesta de verdad cada kilo que podés vender.
            </>
          )}
        </p>
      </CardBody>
    </Card>
  );
}

function CutsComparison({ c }: { c: CarcassInsightDTO }) {
  const sups = c.suppliers.filter((s) => c.cuts.some((x) => (s.supplierId ?? 'none') in x.bySupplier));
  if (sups.length < 2 || !c.cuts.length) return null;
  const rows = c.cuts.slice(0, 10);
  return (
    <Card>
      <CardHeader
        title="Cortes por frigorífico"
        subtitle="Cuánto sale de cada corte por cada 100 kg de media res. Arriba, donde más se diferencian."
      />
      <CardBody flush>
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Corte</th>
                <th className="bf-num">Promedio</th>
                {sups.map((s) => (
                  <th key={s.supplierId ?? 'none'} className="bf-num">
                    {s.supplierName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const vals = sups.map((s) => r.bySupplier[s.supplierId ?? 'none']);
                const best = Math.max(...vals.filter((v): v is number => v !== undefined));
                return (
                  <tr key={r.productId}>
                    <td className="bf-cell-main">{r.name}</td>
                    <td className="bf-num bf-cell-sub">{pts(r.overall)} kg</td>
                    {vals.map((v, i) => (
                      <td key={i} className="bf-num" style={v === best && vals.length > 1 ? { fontWeight: 700 } : undefined}>
                        {v !== undefined ? `${pts(v)} kg` : '—'}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- cortes (plantillas)
function TemplatesTab({ templates }: { templates: CutTemplateDTO[] }) {
  const auto = useSession().me.tenant.modules.includes('CUTTING');
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<CutTemplateDTO | null>(null);
  const [deleting, setDeleting] = useState<CutTemplateDTO | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  async function remove(t: CutTemplateDTO) {
    setDelBusy(true);
    try {
      await api.del(`/cutting/templates/${t.id}`);
      await Promise.all(['cutting', 'products'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Tipo borrado', t.name);
      setDeleting(null);
    } catch (err) {
      toast.error('No se pudo borrar', errorMessage(err));
    } finally {
      setDelBusy(false);
    }
  }
  const [name, setName] = useState('');
  const [animal, setAnimal] = useState<CarcassAnimal>('BEEF');
  const taken = (n: string) => templates.some((t) => t.sourceProductName.toLowerCase() === n.trim().toLowerCase());
  const suggestions = CARCASS_ANIMALS.flatMap((a) => CARCASS_TYPES[a].map((n) => ({ animal: a, name: n }))).filter((x) => !taken(x.name));

  async function create(a: CarcassAnimal, n: string) {
    if (n.trim().length < 3) return;
    setBusy(n);
    try {
      await api.post('/cutting/templates/standard', { animal: a, name: n.trim() });
      await Promise.all(['cutting', 'products'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success(`Lista “${n.trim()}” creada`, 'Ya podés comprar ese tipo de media res y despostarla. Revisá los precios de los cortes.');
      setName('');
    } catch (err) {
      toast.error('No se pudo', errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Tipos de media res que trabajás"
          subtitle="Cada tipo tiene su lista de cortes y su propio rinde, porque no da lo mismo un novillo que una vaca pesada. Al comprar, elegís el tipo como producto (ej.: “Media res novillo”)."
        />
        <CardBody>
          <div className="bf-form" style={{ maxWidth: 720 }}>
            {suggestions.length > 0 && (
              <div className="bf-chips">
                {suggestions.map((x) => (
                  <button
                    key={x.name}
                    type="button"
                    className="bf-chip"
                    aria-pressed={name === x.name}
                    onClick={() => {
                      setName(x.name);
                      setAnimal(x.animal);
                    }}
                  >
                    {x.name}
                  </button>
                ))}
              </div>
            )}
            <div className="bf-form-row">
              <Field
                label="Nombre del tipo"
                hint="Tocá uno de arriba o escribí otro (ej.: Media res vaca conserva). Arranca con los cortes estándar; después sacás o agregás los que hagas."
              >
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Media res …" />
              </Field>
              <Field label="Animal">
                <Select value={animal} onChange={(e) => setAnimal(e.target.value as CarcassAnimal)}>
                  <option value="BEEF">Vacuno</option>
                  <option value="PORK">Cerdo</option>
                </Select>
              </Field>
            </div>
            <div>
              <Button
                icon={<ListChecks />}
                loading={busy === name}
                disabled={name.trim().length < 3 || taken(name)}
                onClick={() => void create(animal, name)}
              >
                Crear lista
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      {templates.map((t) => (
        <Card key={t.id}>
          <CardHeader
            title={t.name}
            subtitle={
              auto
                ? `${t.lines.filter((l) => l.kind === 'CUT').length} cortes · ${
                    t.samples ? `referencia de tus últimos ${t.samples} despostes` : 'referencia estándar (se ajusta sola con tus despostes)'
                  }`
                : `${t.lines.filter((l) => l.kind === 'CUT').length} cortes`
            }
            actions={
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="sm" variant="ghost" onClick={() => setOpen(open === t.id ? null : t.id)}>
                  {open === t.id ? 'Ocultar' : 'Ver cortes'}
                </Button>
                <Button size="sm" onClick={() => setEditing(t)}>
                  Editar cortes
                </Button>
                <Button size="sm" variant="ghost" iconOnly icon={<Trash2 />} onClick={() => setDeleting(t)}>
                  Borrar tipo
                </Button>
              </div>
            }
          />
          {open === t.id && (
            <CardBody flush>
              <table className="bf-table">
                <tbody>
                  {t.lines.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <span className="bf-cell-main">{l.productName ?? l.label}</span> {l.kind === 'TACHO' && <Badge>al tacho</Badge>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {auto && `${l.percent.toLocaleString('es-AR')} %`}
                        {auto && l.learnedPercent !== null && (
                          <div className="bf-cell-sub">
                            de tus despostes ({l.samples}) · al arrancar {l.defaultPercent.toLocaleString('es-AR')} %
                          </div>
                        )}
                      </td>
                      <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                        {l.kind === 'CUT' ? l.priceCents ? `${formatMoney(l.priceCents)}/kg` : <Badge tone="warning">sin precio</Badge> : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          )}
        </Card>
      ))}
      {editing && <TemplateDialog template={editing} onClose={() => setEditing(null)} />}
      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && !delBusy && setDeleting(null)}
        title="Borrar tipo de media res"
        description={`¿Borrar “${deleting?.name ?? ''}” y su lista de cortes? Los cortes (asado, vacío…) no se borran: siguen en Productos. Si ya hubo despostes de este tipo, quedan en el historial.`}
      >
        <div className="bf-form-actions">
          <Button onClick={() => setDeleting(null)} disabled={delBusy}>
            Cancelar
          </Button>
          <Button variant="danger" icon={<Trash2 />} loading={delBusy} onClick={() => deleting && void remove(deleting)}>
            Borrar
          </Button>
        </div>
      </Dialog>
    </>
  );
}

function TemplateDialog({ template, onClose }: { template: CutTemplateDTO; onClose: () => void }) {
  const auto = useSession().me.tenant.modules.includes('CUTTING');
  const qc = useQueryClient();
  const [rows, setRows] = useState(template.lines.map((l) => ({ ...l, value: l.defaultPercent.toString().replace('.', ','), remove: false })));
  const allOn = rows.every((r) => !r.remove);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const total = rows.filter((r) => !r.remove).reduce((a, r) => a + (Number(r.value.replace(',', '.')) || 0), 0);

  async function save() {
    if (rows.every((r) => r.remove)) return setError('Dejá al menos un corte. Si el tipo entero está de más, borralo con el tacho de basura.');
    setBusy(true);
    setError(null);
    try {
      await api.put(`/cutting/templates/${template.id}`, {
        animal: template.animal,
        name: template.name,
        lines: rows
          .filter((r) => !r.remove)
          .map((r) => ({ kind: r.kind, productId: r.productId, label: r.label, percent: Number(r.value.replace(',', '.')) || 0 })),
      });
      await qc.invalidateQueries({ queryKey: ['cutting'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Cortes de ${template.name}`}
      description={
        auto
          ? 'Destildá los cortes que no hacés y agregá los que falten. El porcentaje es solo la referencia para avisarte si un peso quedó mal escrito; al despostar manda lo que pesás.'
          : 'Destildá los cortes que no hacés y agregá los que falten. Es la lista que aparece en tu planilla de desposte.'
      }
      wide
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="sm" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, remove: allOn })))}>
            {allOn ? 'Destildar todos' : 'Tildar todos'}
          </Button>
          <span className="bf-cell-sub">Para borrar el tipo entero, cerrá esto y tocá el tacho de basura.</span>
        </div>
        <div className="bf-scroll-box bf-cut-table">
          <table className="bf-table">
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ opacity: r.remove ? 0.4 : 1 }}>
                  <td>
                    <label className="bf-check">
                      <input
                        type="checkbox"
                        checked={!r.remove}
                        onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, remove: !e.target.checked } : x)))}
                      />{' '}
                      {r.productName ?? r.label}
                    </label>
                  </td>
                  <td style={{ textAlign: 'right', display: auto ? undefined : 'none' }}>
                    <Input
                      inputMode="decimal"
                      value={r.value}
                      onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                      aria-label={`Porcentaje de ${r.productName ?? r.label}`}
                      style={{ maxWidth: 90, marginLeft: 'auto' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AddCutForm
          animal={template.animal}
          allowTacho
          hint={
            auto
              ? 'Los cortes conocidos (entraña, tapa de asado…) toman su % típico. Si es nuevo, poné cuánto suele dar o dejalo en 0: se ajusta solo con el próximo desposte.'
              : undefined
          }
          exclude={rows.map((r) => r.productName ?? r.label ?? '')}
          onAdd={(c) =>
            setRows((rs) => [
              ...rs,
              {
                id: `new:${c.productId ?? c.name}`,
                kind: c.kind,
                productId: c.productId,
                productName: c.kind === 'CUT' ? c.name : null,
                label: c.label,
                defaultPercent: standardCutPercent(template.animal, c.name) ?? 0,
                learnedPercent: null,
                samples: 0,
                percent: standardCutPercent(template.animal, c.name) ?? 0,
                priceCents: null,
                value: (standardCutPercent(template.animal, c.name) ?? 0).toString().replace('.', ','),
                remove: false,
              },
            ])
          }
        />
        <p className="bf-cell-sub" style={{ margin: 0, display: auto ? undefined : 'none' }}>
          Suma: {total.toLocaleString('es-AR', { maximumFractionDigits: 1 })} % · lo que falta hasta 100 % es lo que se suele perder al despostar.
        </p>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
